import { z } from "zod";
import { PACK_VERSION, STAGES, type AuditFile } from "./india";
import { querySchema } from "./queries";
const str = z.string();
const num = z.number().finite();
const applicability = z.object({
  decision: z.enum(["unassessed", "applicable", "not-applicable"]),
  rationale: str,
});
const fileSchema: z.ZodType<AuditFile, z.ZodTypeDef, unknown> = z.object({
  id: str,
  clientId: str,
  clientName: str,
  periodEnd: str,
  framework: z.enum(["AS", "Ind AS"]),
  entity: z.enum(["company", "nbfc", "llp", "other"]),
  consolidated: z.boolean(),
  partner: str,
  stage: z.enum(
    STAGES as [
      "Acceptance",
      "Planning",
      "Fieldwork",
      "Review",
      "Completion",
      "Archived",
    ],
  ),
  version: z.literal(PACK_VERSION),
  createdAt: str,
  updatedAt: str,
  applicability: z.object({
    caro: applicability,
    ifc: applicability,
    kam: applicability,
    auditTrail: applicability,
  }),
  frameworkRationale: str,
  acceptance: z.object({
    independence: z.boolean(),
    ethics: z.boolean(),
    appointment: z.boolean(),
    letterRef: str,
    predecessor: str,
  }),
  materiality: z.object({
    benchmark: str,
    base: num,
    percent: num,
    performancePercent: num,
    trivialPercent: num,
    rationale: str,
  }),
  tb: z.array(
    z.object({ code: str, name: str, current: num, prior: num, mapping: str }),
  ),
  tbSource: str,
  tbReconciled: z.boolean(),
  risks: z.array(
    z.object({
      id: str,
      title: str,
      assertion: str,
      level: z.enum(["Low", "Moderate", "High", "Significant"]),
      response: str,
    }),
  ),
  procedures: z.array(
    z.object({
      id: str,
      area: z.enum([
        "Acceptance",
        "Planning",
        "Financial statements",
        "Substantive",
        "Controls",
        "Completion",
        "CARO",
      ]),
      title: str,
      standard: str,
      objective: str,
      evidence: str,
      conclusion: str,
      riskIds: z.array(str),
      naReason: str,
      status: z.enum(["open", "prepared", "reviewed"]),
      preparedBy: str.optional(),
      preparedAt: str.optional(),
      reviewedBy: str.optional(),
      reviewedAt: str.optional(),
      reviewNote: str.optional(),
    }),
  ),
  requests: z.array(
    z.object({
      id: str,
      title: str,
      owner: str,
      due: str,
      status: z.enum(["requested", "received", "accepted", "rejected"]),
      evidence: str,
      note: str,
    }),
  ),
  findings: z.array(
    z.object({
      id: str,
      title: str,
      amount: num,
      severity: z.enum(["Low", "Moderate", "High"]),
      disposition: z.enum(["open", "adjusted", "unadjusted", "reported"]),
      rationale: str,
    }),
  ),
  queries: z.array(querySchema).default([]),
  completion: z.object({
    opinion: str,
    reportDate: str,
    subsequentThrough: str,
    representationRef: str,
    goingConcern: str,
    opinionRationale: str,
    udin: str,
    qualityReview: str,
  }),
  events: z.array(z.object({ at: str, actor: str, action: str })),
});
export function readDrafts(
  key: string,
  storage: Pick<Storage, "getItem"> = localStorage,
): { files: AuditFile[]; error: string } {
  try {
    const raw = storage.getItem(key);
    if (!raw) return { files: [], error: "" };
    const files = z.array(fileSchema).parse(JSON.parse(raw));
    return { files, error: "" };
  } catch {
    return {
      files: [],
      error:
        "Saved drafts could not be read. Export your browser data before resetting it; no saved data has been overwritten.",
    };
  }
}
